import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useVisitState } from 'store/visits/hooks';
import { DeleteModalV2 } from 'components/v2/delete-modal/delete-modal-v2';
import { useDispatch, useSelector } from 'react-redux';
import { getVisitsLoading } from 'store/visits/selectors';
import { OwnerParams } from 'shared/types';
import { cancelGroupVisit, cancelVisit } from 'store/visits/actions';
import { Visit } from 'store/visits/types';

interface VisitDeleteRouteState {
  visitors?: Visit['visitors'];
  cancelScope?: 'single' | 'group';
}

export const VisitDeletePageV2: FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const { ownerId } = useParams<OwnerParams>();
  const dispatch = useDispatch();
  const visitState = useVisitState(visitId || '', false);
  const visitToCancel = visitState?.data;
  const visitCancelLoading = useSelector(getVisitsLoading);

  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as VisitDeleteRouteState | null;
  const visitorsToCancel = routeState?.visitors;
  const cancelScope = routeState?.cancelScope ?? 'single';
  const visitToDisplay = useMemo(() => {
    if (!visitToCancel) {
      return visitToCancel;
    }
    if (visitorsToCancel?.length) {
      return { ...visitToCancel, visitors: visitorsToCancel };
    }
    return visitToCancel;
  }, [visitToCancel, visitorsToCancel]);

  const handleClose = useCallback(() => {
    navigate('..', { relative: 'route' });
  }, [navigate]);
  const handleCancelVisit = useCallback(() => {
    if (!ownerId) {
      return;
    }
    if (cancelScope === 'group' && visitToCancel?.is_group_visit && visitToCancel.group_visit_uuid) {
      dispatch(
        cancelGroupVisit.request({
          buildingId: ownerId,
          groupVisitUuid: visitToCancel.group_visit_uuid,
        }),
      );
      return;
    }
    if (!visitId) {
      return;
    }
    dispatch(cancelVisit.request({ buildingId: ownerId, visitId }));
  }, [cancelScope, dispatch, ownerId, visitId, visitToCancel]);

  const visitIsRecurring = useMemo(() => {
    return Object.values(visitToDisplay?.recurrence_days || {}).some(Boolean);
  }, [visitToDisplay]);

  return (
    <DeleteModalV2
      handleClose={handleClose}
      handleCancelVisit={handleCancelVisit}
      visitCancelLoading={visitCancelLoading}
      visitToCancel={visitToDisplay}
      visitIsRecurring={visitIsRecurring}
    />
  );
};
